import { useCallback, useEffect, useRef, useState } from "react";
import {
  getHandle,
  hasHandle,
  HandleModal,
  haptic,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
  type SubmitResult,
} from "@karrtopelka/game-kit";
import type { CardProps } from "@karrtopelka/cards/types";

import { buildStopCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import {
  BLIND_AFTER_MS,
  CAP_MS,
  formatStopped,
  TARGET_MS,
  useStopStore,
} from "./store.js";

export function App() {
  const phase = useStopStore((s) => s.phase);
  const reset = useStopStore((s) => s.reset);

  // Warm the card-render assets (2.5MB wasm + fonts) the moment the player starts —
  // memoized, so this never double-fetches. Skipped for idle bouncers.
  const warmed = useRef(false);
  useEffect(() => {
    if (!warmed.current && phase !== "idle") {
      warmed.current = true;
      void loadCardAssets().catch(() => {});
    }
  }, [phase]);

  // Funnel beacons: one page_view per load, one play_start per session, one
  // play_done per finished run.
  useEffect(() => trackPageView("stop"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "running" && !started.current) {
      started.current = true;
      track("stop", "play_start");
    }
    if (phase === "done") track("stop", "play_done");
  }, [phase]);

  // One board instance feeds the mobile summary board AND the desktop rail.
  const board = useLeaderboard("stop");
  const [handle, setHandleState] = useState<string | null>(() => getHandle());

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-6 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:items-start lg:justify-center lg:gap-10 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center lg:col-span-3">
        <h1 className="text-3xl font-extrabold tracking-tight lg:text-5xl">
          stop
          <span className="inline-block animate-breathe text-accent drop-shadow-[0_0_10px_rgba(255,111,165,0.55)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          stop the clock at exactly 5.00s — blind after 2.
        </p>
      </header>

      <InfoRail />

      <div className="flex w-full max-w-md flex-col items-center gap-6 lg:max-w-none">
        {phase === "done" ? (
          <Summary
            board={board}
            handle={handle}
            onHandle={setHandleState}
            onPlayAgain={reset}
          />
        ) : (
          <Clock />
        )}

        {phase !== "done" && (
          <LeaderboardSection board={board} handle={handle} />
        )}
      </div>

      <BoardRail board={board} handle={handle} />

      <footer
        className="mt-4 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-2 lg:gap-3"
        style={{ animationDelay: "320ms" }}
      >
        <div className="hidden items-center gap-3 lg:flex">
          <span className="uppercase tracking-wide">more games</span>
          <GameChip href="https://reflex.karrtopelka.com" name="reflex" hook="react in ms" />
          <GameChip href="https://aim.karrtopelka.com" name="aim" hook="10 targets, go" />
          <GameChip href="https://crash.karrtopelka.com" name="crash" hook="cash out or bust" />
          <a
            href="https://karrtopelka.com"
            className="transition-colors duration-200 hover:text-accent"
          >
            karrtopelka.com →
          </a>
        </div>
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://reflex.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try reflex — reaction test →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>
      </footer>
    </main>
  );
}

/** Desktop-only left rail: rules, personal best, keyboard hint. Quiet cards. */
function InfoRail() {
  const prevBest = useStopStore((s) => s.prevBest);
  return (
    <aside
      className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:w-full lg:flex-col lg:gap-4"
      style={{ animationDelay: "160ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="mt-2 flex flex-col gap-1.5 text-sm text-text-muted">
          <li>tap — the clock starts.</li>
          <li>at 2.00s it goes blind.</li>
          <li>stop at exactly 5.00s.</li>
        </ul>
      </div>
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          your best
        </h2>
        {prevBest !== null ? (
          <p className="mt-1 font-display text-3xl font-extrabold tabular-nums">
            {prevBest}
            <span className="ml-1 text-sm font-normal text-text-muted">
              ms off
            </span>
          </p>
        ) : (
          <p className="mt-1 text-sm text-text-faint">no runs yet — set one.</p>
        )}
      </div>
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          keyboard
        </h2>
        <p className="mt-2 text-sm text-text-muted">
          <kbd className="rounded-md border border-border bg-surface-raised px-2 py-0.5 font-mono text-xs text-text">
            space
          </kbd>{" "}
          starts &amp; stops.
        </p>
      </div>
    </aside>
  );
}

/** Desktop-only right rail: the leaderboard, persistent through play AND results. */
function BoardRail({ board, handle }: { board: Board; handle: string | null }) {
  return (
    <aside
      className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:w-full lg:flex-col lg:gap-3"
      style={{ animationDelay: "240ms" }}
    >
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          best stops
        </h2>
        <Leaderboard
          entries={board.entries}
          loading={board.loading}
          myHandle={handle}
          emptyLabel="no one's stopped the clock yet"
          formatValue={(v) => `${v}ms off`}
          range={board.range}
          onRange={board.setRange}
        />
      </div>
    </aside>
  );
}

function GameChip({
  href,
  name,
  hook,
}: {
  href: string;
  name: string;
  hook: string;
}) {
  return (
    <a
      href={href}
      className="group flex flex-col rounded-lg border border-border bg-surface/60 px-4 py-2 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
    >
      <span className="text-sm font-semibold text-text transition-colors duration-200 group-hover:text-accent">
        {name}
      </span>
      <span className="text-xs text-text-faint">{hook}</span>
    </a>
  );
}

/** Dial geometry for the clock face SVG (viewBox 0 0 100 100). */
const DIAL_R = 46;
const DIAL_C = 2 * Math.PI * DIAL_R;

/**
 * The clock: one full-width tap zone with a neon dial face. Tap (or space/enter)
 * starts it; a pink arc sweeps toward the 5.00s tick at 12 o'clock until the readout
 * goes blind at 2.00s — then the whole dial breathes and you're on your own. An rAF
 * drives the readout + the CAP_MS auto-stop — the store itself stays timer-free.
 */
function Clock() {
  const phase = useStopStore((s) => s.phase);
  const startedAt = useStopStore((s) => s.startedAt);
  const prevBest = useStopStore((s) => s.prevBest);
  const start = useStopStore((s) => s.start);
  const stop = useStopStore((s) => s.stop);

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (phase !== "running" || startedAt === null) return;
    let raf = 0;
    const tick = () => {
      const e = performance.now() - startedAt;
      if (e >= CAP_MS) {
        // Walked away — close the run at the cap instead of counting forever.
        stop(startedAt + CAP_MS);
        return;
      }
      setElapsed(e);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, startedAt, stop]);

  const onTap = useCallback(() => {
    haptic.tap();
    const p = useStopStore.getState().phase;
    if (p === "idle") {
      setElapsed(0);
      start(performance.now());
    } else if (p === "running") {
      stop(performance.now());
    }
  }, [start, stop]);

  // Desktop affordance: space/enter start + stop the clock. preventDefault keeps
  // space from scrolling AND from re-firing the focused button's synthetic click.
  // Focused links/tabs keep their native key behavior — only the page + the clock
  // surface itself are hijacked.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (e.code !== "Space" && e.code !== "Enter") return;
      const t = e.target;
      if (
        t instanceof HTMLElement &&
        t.closest("a, input, textarea, select, button") &&
        !t.closest("[data-clock]")
      ) {
        return;
      }
      e.preventDefault();
      onTap();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onTap]);

  const blind = phase === "running" && elapsed >= BLIND_AFTER_MS;
  // Sweep fraction of the way to 5.00s — only while the readout is public, so the
  // dial never leaks the time once blind.
  const frac = phase === "running" && !blind ? Math.min(elapsed / TARGET_MS, 1) : 0;

  return (
    <button
      type="button"
      data-clock
      onClick={onTap}
      className={`relative flex aspect-square w-full max-w-md animate-fade-up select-none flex-col items-center justify-center gap-3 rounded-xl border border-border bg-surface transition-all duration-200 hover:border-accent/60 active:scale-[0.99] lg:max-w-lg ${
        phase === "idle" ? "animate-neon-pulse hover:-translate-y-0.5" : ""
      }`}
      style={{ animationDelay: "80ms" }}
    >
      {/* Dial face: hairline tick ring, 5.00s target tick at 12 o'clock, and a pink
          sweep arc that dies when the clock goes blind. */}
      <svg
        viewBox="0 0 100 100"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full p-3"
      >
        <circle
          cx="50"
          cy="50"
          r={DIAL_R}
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          pathLength={60}
          strokeDasharray="0.12 0.88"
          className="text-border"
        />
        <line
          x1="50"
          y1="1.5"
          x2="50"
          y2="7"
          stroke="#ff6fa5"
          strokeWidth="1.6"
          strokeLinecap="round"
          className={blind ? "opacity-40" : "drop-shadow-[0_0_4px_rgba(255,111,165,0.6)]"}
        />
        {blind && (
          <circle
            cx="50"
            cy="50"
            r={DIAL_R}
            fill="none"
            stroke="#ff6fa5"
            strokeWidth="1.2"
            className="animate-breathe opacity-30"
          />
        )}
        {phase === "running" && !blind && (
          <circle
            cx="50"
            cy="50"
            r={DIAL_R}
            fill="none"
            stroke="#ff6fa5"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeDasharray={DIAL_C}
            strokeDashoffset={DIAL_C * (1 - frac)}
            transform="rotate(-90 50 50)"
            className="drop-shadow-[0_0_6px_rgba(255,111,165,0.6)]"
          />
        )}
      </svg>

      {phase === "idle" ? (
        <>
          <span className="text-2xl font-bold lg:text-3xl">tap to start</span>
          <span className="px-6 text-sm text-text-muted lg:text-base">
            the clock hides at 2.00s. stop it at exactly{" "}
            <span className="font-mono text-text">5.00s</span>.
          </span>
          {prevBest !== null && (
            <span className="text-xs text-text-faint">
              your best: <span className="font-mono">{prevBest}ms</span> off
            </span>
          )}
        </>
      ) : (
        <>
          <span
            className={`font-display text-7xl font-extrabold tabular-nums lg:text-8xl ${
              blind ? "animate-breathe text-accent" : ""
            }`}
          >
            {blind ? "?.???s" : `${(elapsed / 1000).toFixed(2)}s`}
          </span>
          <span className="text-sm text-text-muted">
            {blind ? "now you're on your own — tap at 5" : "watch it… it hides at 2"}
          </span>
        </>
      )}
    </button>
  );
}

type Board = ReturnType<typeof useLeaderboard>;

function Summary({
  board,
  handle,
  onHandle,
  onPlayAgain,
}: {
  board: Board;
  handle: string | null;
  onHandle: (h: string) => void;
  onPlayAgain: () => void;
}) {
  const stoppedMs = useStopStore((s) => s.stoppedMs);
  const delta = useStopStore((s) => s.delta);
  const isNewBest = useStopStore((s) => s.isNewBest);
  const prevBest = useStopStore((s) => s.prevBest);

  const [showHandle, setShowHandle] = useState(false);
  const [submitted, setSubmitted] = useState<SubmitResult | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const d = delta ?? 0;
  const stopped = stoppedMs ?? TARGET_MS;
  const early = stopped < TARGET_MS;

  const doSubmit = async () => {
    setPosting(true);
    setSubmitError(null);
    const res = await board.submit(d);
    setPosting(false);
    const who = getHandle() ?? "you";
    if (!res) {
      setSubmitError("couldn't submit (rate limited?) — card still works");
      setCard(buildStopCard(d, who, null));
      return;
    }
    setSubmitted(res);
    setCard(buildStopCard(d, who, res));
  };

  const onPost = () => {
    if (hasHandle()) void doSubmit();
    else setShowHandle(true);
  };

  return (
    <div className="flex w-full max-w-md animate-fade-up flex-col items-center gap-8 lg:max-w-lg">
      <section className="flex flex-col items-center">
        {isNewBest && (
          <span className="mb-2 animate-pop rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg">
            ⚡ new personal best
          </span>
        )}
        <span
          className={`font-display text-7xl font-extrabold tabular-nums lg:text-8xl ${
            isNewBest ? "animate-glow text-accent" : ""
          }`}
        >
          {d}ms
        </span>
        <span className="mt-1 text-sm text-text-muted">
          off the 5.00s stop — you hit{" "}
          <span className="font-mono text-text">{formatStopped(stopped)}</span>
          {d > 0 && <> ({early ? "early" : "late"})</>}
          {!isNewBest && prevBest !== null && d > prevBest && (
            <> · your best {prevBest}ms</>
          )}
        </span>
        {/* Target to beat, visible without scrolling to the full board below. */}
        {board.entries[0] && board.entries[0].value < d && (
          <span className="mt-2 text-xs text-text-faint">
            #1 is{" "}
            <span className="font-mono text-text-muted">
              {board.entries[0].value}ms off
            </span>{" "}
            by {board.entries[0].handle}
          </span>
        )}
        {board.entries[0] && board.entries[0].value >= d && (
          <span className="mt-2 text-xs text-accent">👑 you hold #1</span>
        )}
      </section>

      {submitted && (
        <p className="text-sm text-text">
          #{submitted.rank} of {submitted.total}
          {submitted.total >= 10 && (
            <span className="text-accent"> · top {submitted.percentile}%</span>
          )}
        </p>
      )}
      {submitError && <p className="text-sm text-accent">{submitError}</p>}

      {card ? (
        <ShareSheet
          card={card}
          replyLink="stop.karrtopelka.com/r"
          caption={`${card.display} off the 5.00s stop. ${card.provocation}`}
          loadAssets={loadCardAssets}
        />
      ) : (
        <div className="flex w-full flex-col items-center gap-2">
          <button
            type="button"
            onClick={onPost}
            disabled={posting}
            className="w-full animate-neon-pulse rounded-md bg-accent px-4 py-3 font-semibold text-bg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow disabled:opacity-40"
          >
            {posting ? "posting…" : "post your result"}
          </button>
          {handle && (
            <p className="text-xs text-text-faint">
              posting as <span className="text-text-muted">@{handle}</span>
            </p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setSubmitted(null);
          setCard(null);
          setSubmitError(null);
          onPlayAgain();
        }}
        className="text-sm text-text-muted transition-colors duration-200 hover:text-accent"
      >
        play again
      </button>

      <LeaderboardSection board={board} handle={handle} />

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doSubmit();
          }}
        />
      )}
    </div>
  );
}

/** Mobile leaderboard section — on lg the persistent right rail takes over. */
function LeaderboardSection({
  board,
  handle,
}: {
  board: Board;
  handle: string | null;
}) {
  return (
    <section
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-3 border-t border-border pt-6 lg:hidden"
      style={{ animationDelay: "160ms" }}
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
        leaderboard
      </h2>
      <Leaderboard
        entries={board.entries}
        loading={board.loading}
        myHandle={handle}
        emptyLabel="no one's stopped the clock yet"
        formatValue={(v) => `${v}ms off`}
        range={board.range}
        onRange={board.setRange}
      />
    </section>
  );
}
